import React, { useState, useEffect, useRef } from "react";
import { FiMoreHorizontal, FiSearch, FiFilter, FiArrowUp, FiArrowDown, FiList, FiGrid } from "react-icons/fi";
import { Eye, Pencil, Upload, Trash2, UploadIcon, Copy } from 'lucide-react';
import SkillCard from "../../components/Common/CommonCard";
import ReusableTable from "../../components/Common/BaseTable";
import Breadcrumb from "../../components/Common/Bredcrump";
import BaseButton from "../../components/Common/BaseButton";
import UnifiedDocumentModal from "../../components/Common/UnifiedDocumentModal";
import chatIcon from "../../assets/chat.png";
import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { getAllChats } from "../../utils/chat";
import "./Chat.css";

const Chat = () => {
  const navigate = useNavigate();
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [chatData, setChatData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChatData, setSelectedChatData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Filter chat data based on search term
  const filteredChatData = chatData.filter(chat => 
    chat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    chat.platform.toLowerCase().includes(searchTerm.toLowerCase()) ||
    chat.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
    chat.created.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
  };

  const handleDelete = (selectedRows) => {
    console.log("Delete:", selectedRows);
  };

  const handleDuplicate = (selectedRows) => {
    console.log("Duplicate:", selectedRows);
  };

  const handleMenuOption = (action, rowData) => {
    console.log(`${action} clicked for`, rowData);
    setSelectedRow(null);
    
    if (action === 'edit') {
      setSelectedChatData(rowData);
      setShowDocumentModal(true);
    }
  };

  const skills = [
    { title: "Chat", icon: chatIcon },
  ];

  const handleSkillClick = (title) => {
    if (title === "Chat"){
      setSelectedChatData(null); 
      setShowDocumentModal(true);
    }
  };

  // Handle clicks outside action menu to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the clicked element is part of an action menu
      const isActionMenuClick = event.target.closest('.action-menu') || 
                               event.target.closest('.three-dot-menu') ||
                               event.target.closest('[data-action-menu]');
      
      if (!isActionMenuClick) {
        setSelectedRow(null);
      }
    };

    if (selectedRow !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedRow]);

  // Fetch chat data from API
  useEffect(() => {
    const fetchChatData = async () => {
      try {
        setLoading(true);
        const response = await getAllChats();
                
        // Transform API response to match table structure
        if (response && Array.isArray(response)) {
          const transformedData = response.map((chat, index) => ({
            id: chat.id || index + 1,
            name: chat.title || chat.name || `Chat ${index + 1}`,
            created: chat.created_at ? new Date(chat.created_at).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: '2-digit'
            }) : 'N/A',
            platform: "Chat",
            status: chat.status || "Published",
            uid: chat.uid,
            agent_id: chat.agent_id,
            session_id: chat.session_id
          }));
          setChatData(transformedData);
        } else if (response && response.data && Array.isArray(response.data)) {
          const transformedData = response.data.map((chat, index) => ({
            id: chat.id || index + 1,
            name: chat.title || chat.name || `Chat ${index + 1}`,
            created: chat.created_at ? new Date(chat.created_at).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: '2-digit'
            }) : 'N/A',
            platform: "Chat",
            status: chat.status || "Published",
            uid: chat.uid,
            agent_id: chat.agent_id,
            session_id: chat.session_id
          }));
          setChatData(transformedData);
        } else {
          console.log('Unexpected response format:', response);
          setChatData([]);
        }
      } catch (error) {
        console.error('Error fetching chat data:', error);
        setChatData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchChatData();
  }, []);

  // Log when chat component is opened
  useEffect(() => {
    console.clear();
    console.log("chat opened");
  }, []);

  const columns = [
    {
      id: "selection",
      header: ({ table }) => (
        <input
          type="checkbox"
          checked={table.getIsAllPageRowsSelected()}
          onChange={() => table.toggleAllPageRowsSelected()}
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={row.getIsSelected()}
          onChange={() => row.toggleSelected()}
        />
      ),
      size: "5%",
    },
    {
      accessorKey: "name",
      header: "Name",
    },
    {
      accessorKey: "created",
      header: "Created",
    },
    {
      accessorKey: "platform",
      header: "Platform",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => {
        const status = getValue();
        const statusClass =
          status === "Published" ? "status-published" : "status-draft";
        const dotColor = status === "Published" ? "green" : "grey";
        return (
          <span className={`status-badge ${statusClass}`}>
            <span style={{ color: dotColor, marginRight: '5px' }}>•</span>
            {status}
          </span>
        );
      },
    },
    {
      header: "Actions",
      cell: ({ row }) => {
        return (
          <div style={{ position: 'relative' }} data-action-menu>
            <FiMoreHorizontal 
              className="three-dot-menu" 
              onClick={(e) => {
                e.stopPropagation();
                setSelectedRow(selectedRow === row.index ? null : row.index);
              }}
              style={{ cursor: 'pointer' }}
            />
            {selectedRow === row.index && (
              <div className="action-menu">
                <button 
                  className="menu-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuOption('preview', row.original);
                  }}
                >
                  <Eye size={16} />
                  <span>Preview</span>
                </button>
                
                <button 
                  className="menu-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuOption('edit', row.original);
                  }}
                >
                  <Pencil size={16} />
                  <span>Edit</span>
                </button>

                <button 
                  className="menu-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuOption('document', row.original);
                  }}
                >
                  <UploadIcon size={16} />
                  <span>Document</span>
                </button>
                
                <button 
                  className="menu-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuOption('publish', row.original);
                  }}
                >
                  <Upload size={16} />
                  <span>Publish</span>
                </button>
                
                <button 
                  className="menu-item delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuOption('delete', row.original);
                  }}
                >
                  <Trash2 size={16} />
                  <span>Delete</span>
                </button>
              </div>
            )}
          </div>
        );
      },
    },
  ];
  

  return (
    <div className="chat-page">
      <Breadcrumb />
      
      {/* Skills Container */}
      <div className="skills-container">
        {skills.map((skill, index) => (
          <SkillCard
            key={skill?.title}
            icon={skill.icon}
            title={skill.title}
            onClick={() => handleSkillClick(skill.title)}
          />
        ))}
      </div>

      {/* Generated Content Section */}
      <div className="generated-content-section">
        <div className="content-header">
          <h2 className="title-table-header">Generated content</h2>
          <BaseButton 
            className="start-conversation-btn"
            variant="primary"
            onClick={() => {
              setSelectedChatData(null); 
              setShowDocumentModal(true);
            }}
          >
            <span className="plus-icon">+</span>
            Start a new conversation
          </BaseButton>
        </div>

      

        {/* Table */}
        <div className="table-container">
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center' }}>
              <p>Loading chat data...</p>
            </div>
          ) : (
            <ReusableTable
              data={filteredChatData}
              columns={columns}
              onSearch={handleSearch}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
              gridView={false}
            />
          )}
        </div>
      </div>

      {/* Document Upload Modal */}
      <UnifiedDocumentModal
        show={showDocumentModal}
        onHide={() => {
          setShowDocumentModal(false);
          setSelectedChatData(null);
        }}
        initialTags=""
        initialDocList=""
        editingRowData={selectedChatData}
      />
    </div>
  );
};

export default Chat;
